import React, { useMemo } from "react";
import { FillPct, Loc } from "./types";
import { bucketize } from "./utils";

type Props = {
  data: Loc[];
  resolveNivel: (l: Loc) => number;
  compact?: boolean;
};

const COLORS: Record<FillPct, string> = {
  0: "#e5e7eb",
  25: "#fb923c",
  50: "#facc15",
  75: "#86efac",
  100: "#16a34a",
};
const labels: Record<FillPct, string> = {
  0: "0%",
  25: "25%",
  50: "50%",
  75: "75%",
  100: "100%",
};
const order: FillPct[] = [0, 25, 50, 75, 100];

const DepotPie: React.FC<Props> = ({ data, resolveNivel, compact }) => {
  const stat = useMemo(() => {
    const counts: Record<FillPct, number> = {
      0: 0,
      25: 0,
      50: 0,
      75: 0,
      100: 0,
    };
    for (const l of data) {
      const lvl = bucketize(resolveNivel(l));
      counts[lvl]++;
    }
    const total = data.length || 0;
    const parts = order.map((k) => ({
      level: k,
      count: counts[k],
      pct: total ? Math.round((counts[k] / total) * 100) : 0,
    }));
    return { total, parts };
  }, [data, resolveNivel]);

  const size = compact ? 130 : 160;
  const cx = size / 2,
    cy = size / 2,
    r = compact ? 46 : 56,
    strokeW = compact ? 14 : 16;
  let start = -Math.PI / 2;

  return (
    <div className={`wm-pie ${compact ? "wm-pie--compact" : ""}`}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="wm-pie-svg"
        aria-hidden="true">
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="#f1f5f9"
          strokeWidth={strokeW}
        />
        {stat.parts.map(({ level, count }) => {
          if (!count || !stat.total) return null;
          const frac = count / stat.total;
          const end = start + frac * Math.PI * 2;
          const x1 = cx + r * Math.cos(start),
            y1 = cy + r * Math.sin(start);
          const x2 = cx + r * Math.cos(end),
            y2 = cy + r * Math.sin(end);
          const largeArc = frac > 0.5 ? 1 : 0;
          const d = `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
          start = end;
          return (
            <path
              key={level}
              d={d}
              fill="none"
              stroke={COLORS[level]}
              strokeWidth={strokeW}
            />
          );
        })}
        <circle cx={cx} cy={cy} r={r - strokeW / 2} fill="#fff" />
        <text x={cx} y={cy - 2} textAnchor="middle" className="wm-pie-center1">
          Distribuție
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" className="wm-pie-center2">
          {stat.total} celule
        </text>
      </svg>

      {!compact && (
        <div className="wm-pie-legend">
          {stat.parts.map(({ level, pct, count }) => (
            <div key={level} className="wm-pie-legend-item">
              <i className={`wm-swatch wm-swatch--${level}`} />
              <span className="wm-pie-legend-label">{labels[level]}</span>
              <span className="wm-pie-legend-val">{pct}%</span>
              <span className="wm-pie-legend-count">({count})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DepotPie;
